import jwtDecode from 'jwt-decode';
import Cookie from 'universal-cookie';

// Defaults
// NATWEB also uses this same cookie, so if changes are made need to coordinate with them
const DEFAULT_TOKEN_NAME = 'FOXSSOACCT';
const PREPROD_TOKEN_NAME = 'qFOXSSOACCT';
const SECURE_PREFIX = '__Secure-';

const DEFAULT_AUTH_OPTIONS = {
  name: DEFAULT_TOKEN_NAME,
  path: '/',
  preProd: false,
  secure: true,
};

// Auth (simple sso)
class Auth {
  jwt;
  options = DEFAULT_AUTH_OPTIONS;
  store;
  token;

  constructor() {
    this.store = new Cookie();
  }

  logDebug(name, data) {
    console.debug(`${name}:`, data);
  }

  logError(error) {
    console.error(error);
  }

  getCookieDomain() {
    // add dot prefix to support subdomains for non-localhost hostname
    const hostname = window?.location?.hostname || '';
    return hostname.includes('local') ? hostname : `.${hostname}`;
  }

  configure(config = {}) {
    this.options = Object.assign({}, this.options, config);
    if (this.options?.preProd) {
      this.options.name = PREPROD_TOKEN_NAME;
    }
    this.checkSecure();
    this.loadCookie();
    this.logDebug('Auth', 'config');
    return this.token;
  }

  getToken() {
    this.logDebug('Auth', 'get');
    return this.token;
  }

  setToken(token) {
    try {
      if (token && this.options?.name) {
        const expires = token?.tokenExpiration ? new Date(token.tokenExpiration) : undefined;
        const domain = this.options?.domain || this.getCookieDomain();
        const options = { ...this.options, domain, expires };

        if (token?.accessToken) {
          this.jwt = jwtDecode(token?.accessToken);
        }
        this.token = token;
        this.store.set(this.options?.name, token, options);
        this.logDebug('Auth', 'set');
      }
    } catch (error) {
      this.logError(error);
    }
  }

  removeToken() {
    if (this.options?.name) {
      const domain = this.options?.domain || this.getCookieDomain();
      this.store.remove(this.options?.name, { ...this.options, domain });
      this.store.remove(this.options?.name);
    }
    this.token = undefined;
    this.jwt = undefined;
    this.logDebug('Auth', 'remove');
    return undefined;
  }

  getMvpdId() {
    return this.jwt?.mvpdid;
  }

  getUserId() {
    return this.jwt?.uid;
  }

  getDeviceId() {
    return this.jwt?.did;
  }

  getUserType() {
    return this.jwt?.utype;
  }

  getDeviceType() {
    return this.jwt?.dtype;
  }

  getAccountType() {
    return this.jwt?.atype;
  }

  getAccessToken() {
    this.logDebug('Auth', 'get token');
    return this.token?.accessToken;
  }

  getMvpdAccessToken() {
    this.logDebug('Auth', 'get mvpd token');
    if (this.isMvpdUser()) {
      return this.token?.accessToken;
    }
    return undefined;
  }

  getNonAnonAccessToken() {
    this.logDebug('Auth', 'get identity token');
    if (this.isNonAnonymousUser()) {
      return this.token?.accessToken;
    }
    return undefined;
  }

  getAnonDeviceId() {
    const isAnon = this.isAnonymousUser();
    const deviceId = this.getDeviceId();
    if (isAnon) {
      return deviceId;
    }
    return undefined;
  }

  isAnonymousUser() {
    return !!this.jwt?.atype && this.jwt.atype === 'anonymous';
  }

  isNonAnonymousUser() {
    return !!this.jwt?.atype && this.jwt.atype !== 'anonymous';
  }

  isMvpdUser() {
    return this.isAnonymousUser() && !!this.getMvpdId();
  }

  checkSecure() {
    if (this.options?.name && this.options.secure && !this.options.name.includes(SECURE_PREFIX)) {
      this.options.name = SECURE_PREFIX + this.options.name;
      this.options.sameSite = true;
    }
  }

  loadCookie() {
    if (this.options?.name) {
      const token = this.store.get(this.options.name);
      this.setToken(token);
    }
  }
}
const AuthStore = new Auth();
export { AuthStore };
